import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Grid,
  DatePicker,
  Typography,
  Flex,
  Radio,
  AutoComplete,
  Select,
  Button,
  message,
  Drawer,
  Tooltip,
  Checkbox
} from "antd";
import { useForm } from "antd/es/form/Form";
import InputField from "../../../../../components/Input/Input";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { QuestionCircleOutlined } from "@ant-design/icons";

import {
  fetchPurchaseReturnNumber,
  fetchSalesReturnAccounts,
  fetchPurchaseReturnAccs,
  fetchPurchaseReturnRecord,
  fetchSupplierInvoices,
  fetchInvoiceItemsForReturn,
} from "../purchaseReturnThunk";
import {
  setSupplier,
  setFormField,
  setEntryDate,
  setDueDate,
  setTaxMode,
  addOrderRow,
  calculateTotals,
  setTaxAcc,
  setDiscountAcc,
  setPurchaseReturnAcc,
  setSelectedInvoice,
  resetPurchaseReturnFields,
  setInvoiceList,
  setTagFeildNameid,
  setPurIid,
  setIsTatmeen,
  setParentSsccCode,
} from "../purchaseReturnSlice";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import {
  fetchItems,
  fetchSuppliers,
  fetchTaxAccs,
  fetchDiscountAccs,
  fetchTagGroupFields,
  fetchTagsOnEdit,
} from "../../../../../store/commonThunks/lookups";
import {
  selectDiscountAccs,
  selectSuppliers,
  selectTaxAccs,
} from "../../../../../store/commonSelectors/lookupsSelectors";
import StaticAutoComplete from "../../../../../components/StaticAutoComplete";
import { accessChecker, dateTimeFormat, formatToCompanyTimezone, getYearFromCompanyDate, pathSpliter } from "../../../../../lib/functions";
import TagCreateForm from "../../../../Settings/Tags/components/TagCreateForm";
import AddNewModal from "../../../../../components/DropDown/AddNewModal";
import useMigrationDate from "../../../../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;

// inside the same file as CreatePurchaseReturnForm:
const SupplierAutoComplete = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lookups = useSelector((s) => s.lookups);
  const suppliers = useSelector((s) => selectSuppliers(s));
  const supplierInfo = useSelector((s) => s.purchaseReturn.supplierInfo);
  const itemCart = useSelector((s) => s.purchaseReturn.itemCart || []);

  // local controlled input like your BatchNameCell
  const [inputValue, setInputValue] = React.useState(
    supplierInfo?.acc_name || supplierInfo?.institution_name || ""
  );



  // sync local input when supplierInfo changes (e.g., when invoice selected)
  useEffect(() => {
    setInputValue(
      supplierInfo?.acc_name || supplierInfo?.institution_name || ""
    );
  }, [
    supplierInfo?.acc_id,
    supplierInfo?.acc_name,
    supplierInfo?.institution_name,
  ]);

  // build options (plus Add New option)
  const options = [
    // { label: "Add New Supplier", value: "add_new", raw: "add_new" },
    ...(suppliers || []).map((s) => ({
      // label: s.acc_name || s.institution_name,
      label: [s.acc_name, s.contact_no, s.institution_name]
        .filter(Boolean)
        .join(" - "),
      value: s.acc_id,
      id: s.acc_id,
      raw: s,
    })),
  ];

  // helper to handle selection of a supplier object
  const commitSupplier = (sup) => {
    if (!sup) {
      dispatch(fetchSupplierInvoices({ supplierId: "", searchTerm: "" }));
      dispatch(setSupplier(null));
      dispatch(resetPurchaseReturnFields());
      return;
    }
    // reset fields that should be cleared when supplier changes
    dispatch(resetPurchaseReturnFields());

    // set supplier in state
    dispatch(setSupplier(sup));

    // fetch dependent data

    dispatch(fetchSupplierInvoices({ supplierId: sup.acc_id, searchTerm: "" }));
    dispatch(fetchItems()); // prefetch items

    // add first blank row if table empty (mirrors original logic)
    if (!itemCart || itemCart.length === 0) {
      const newRow = {
        key: Date.now(),
        item_id: null,
        product: "",
        quantity: 1,
        rate: 0,
        tax_per: 0,
        discount: 0,
        bonus: false,
      };
      dispatch(addOrderRow(newRow));
    }

    // update the visible input
    setInputValue(sup.acc_name, "");
  };

  // onSelect from AutoComplete gives the selected value (string)
  const handleSelect = (value, obj) => {
    if (value === "add_new") {
      navigate("/purchase"); // send to add new supplier page
      return;
    }

    // find supplier by display name (acc_name or institution_name)
    const found = (suppliers || []).find((s) => s.acc_id === value);

    if (found) {
      commitSupplier(found);
    } else {
      // fallback: clear supplier if nothing matched
      dispatch(setSupplier(null));
      setInputValue(value);
    }
  };

  // typing handler
  const handleChange = (value) => {
    if (!value) {
      commitSupplier(null);
    }
    setInputValue(value);
    if (!value) {
      // cleared manually -> clear supplier selection in redux
      dispatch(setSupplier(null));
    }
  };

  // on blur: if user typed exact existing supplier, commit it; otherwise leave typed text
  const handleBlur = () => {
    const trimmed = (inputValue || "").trim();
    if (!trimmed) {
      dispatch(setSupplier(null));
      return;
    }
    const found = (suppliers || []).find(
      (s) => s.acc_name === trimmed || s.institution_name === trimmed
    );
    if (found) {
      commitSupplier(found);
    } else {
      // user typed a free-text supplier (optional: we clear selection)
      // you can choose to leave free text or clear selection. I'll clear to avoid mismatch:
      dispatch(setSupplier(null));
      setInputValue(trimmed);
    }
  };

  return (
    <AutoComplete
      style={{ width: "100%" }}
      options={options}
      placeholder="Supplier"
      value={inputValue}
      filterOption={(input, option) =>
        (option?.label ?? "")
          .toUpperCase()
          .includes(String(input).toUpperCase())
      }
      onSelect={(value, option) => {
        handleSelect(value, option);
      }}
      onChange={(value, option) => {
        handleChange(value, option);
      }}
      // onSelect={handleSelect}
      // onChange={handleChange}
      onBlur={handleBlur}
      allowClear
    />
  );
};

const CreatePurchaseReturnForm = ({ authInfo, pur_r_id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const screens = useBreakpoint();
  const location = useLocation();

  const lookups = useSelector((s) => s.lookups);
  const [tagModal, setTagModal] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);


  const {
    entryDate,
    dueDate,
    supplierInfo,
    invoiceList,
    selectedInvoice,
    narration,
    voucherNumber,
    taxMode,
    itemCart,
    purchaseReturnAccs,
    selectedTaxAcc,
    selectedPurchaseReturnAcc,
    tag_field_id,
    isTatmeen,
    parentSsccCode
  } = useSelector((s) => s.purchaseReturn); // ✅ include itemCart here

  const suppliers = useSelector(selectSuppliers);
  const { disabledDate } = useMigrationDate();

  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  let tags =
    lookups.tagFields.map((tag) => ({
      label: tag.display_text,
      value: tag.id,
      tagid: tag.id,
    })) || [];

  useEffect(() => {
    if (!pur_r_id) {
      let formatedDate = entryDate ? dayjs(entryDate).toISOString() : null;
      let companydate = formatToCompanyTimezone(formatedDate);
      const year = getYearFromCompanyDate(companydate);
      dispatch(fetchPurchaseReturnNumber(year));
    }
  }, [entryDate]);

  useEffect(() => {
    dispatch(fetchTagGroupFields(""));
    dispatch(fetchPurchaseReturnAccs()).then((res) => {
      if (res?.payload?.length) {
        // ✅ Auto-select first account as default
        const defaultAcc =
          res.payload.find(a => a.acc_name?.toLowerCase().includes("purchase return"))
          || res.payload[0];
        dispatch(setPurchaseReturnAcc(defaultAcc));
        dispatch(setFormField({ field: "selectedPurchaseReturnAcc", value: defaultAcc }));
      }
    });
    dispatch(fetchSuppliers());
    dispatch(fetchItems());
    dispatch(fetchSupplierInvoices({ supplierId: "", searchTerm: "" }));
    dispatch(fetchTaxAccs()).then((res) => {
      if (res.payload && res.payload.length > 0) {
        dispatch(setTaxAcc(res.payload[0])); // pick first as default
      }
    });
    dispatch(fetchDiscountAccs()).then((res) => {
      if (res.payload && res.payload.length > 0) {
        dispatch(setDiscountAcc(res.payload[0])); // pick first as default
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (pur_r_id) {
      dispatch(fetchPurchaseReturnRecord(pur_r_id));
      dispatch(fetchTagsOnEdit({
        tab_id: pur_r_id,
        type: "purchase_return"
      }))
        .unwrap()
        .then((tags) => {
          const tagIds = tags.map(t => t.id);
          dispatch(setTagFeildNameid(tagIds));
        });
    }
  }, [location, dispatch]);

  // selecting default purchasereturnaccount
  // useEffect(() => {
  //   if (purchaseReturnAccs && purchaseReturnAccs.length > 0) {
  //     // Only set default if not already selected
  //     // dispatch(setPurchaseReturnAcc(purchaseReturnAccs[0]));
  //     dispatch(
  //       setFormField({
  //         field: "selectedPurchaseReturnAcc",
  //         value: purchaseReturnAccs[0],
  //       })
  //     );
  //   }
  // }, [purchaseReturnAccs, dispatch]);



  const handleFieldChange = (field, value) => {
    dispatch(setFormField({ field, value }));
  };

  const handleSupplierSelect = (sup) => {
    if (!sup) {
      dispatch(setSupplier(null));
      return;
    }
    if (sup === "add_new") {
      navigate("/purchase");
      return;
    }
    dispatch(resetPurchaseReturnFields());
    // set supplier in state ✅
    dispatch(setSupplier(sup));

    // fetch invoices for this supplier

    dispatch(fetchSupplierInvoices({ supplierId: sup.acc_id, searchTerm: "" }));

    // ✅ add first blank row if table is empty
    if (itemCart.length === 0) {
      const newRow = {
        key: Date.now(),
        item_id: null,
        product: "",
        quantity: 1,
        rate: 0,
        tax_per: 0,
        discount: 0,
        bonus: false,
      };
      dispatch(fetchItems());
      // dispatch(addOrderRow(newRow));
    }
  };

  const handletaggroup = (e, selectedOption) => {
    const value = selectedOption?.map((tag) => tag.tagid);

    dispatch(setTagFeildNameid(value));
  };

  return (
    <div>
      <Flex justify="space-between" align="center">
        <Title level={3} >
          Purchase Return
        </Title>

        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </Flex>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Search Supplier">
              {/* <SearchListAutoComplete
                entityType="supplier"
                placeholder="Supplier"
                optionsList={(suppliers || []).map((s) => ({
                  sale_id: s.acc_id,
                  display_text: s.acc_name || s.institution_name,
                  raw: s,
                }))}
                value={
                  supplierInfo
                    ? {
                      sale_id: supplierInfo.acc_id,
                      display_text: supplierInfo.acc_name || supplierInfo.institution_name,
                      raw: supplierInfo,
                    }
                    : null
                }
                onSelect={(obj) => handleSupplierSelect(obj?.raw)}
                showAddNew={true}
              /> */}
              <SupplierAutoComplete />
            </Form.Item>

            <Form.Item label="Search Voucher" required>
              <SearchListAutoComplete
                data-testid="invoice-voucher-autocomplete"
                placeholder="Invoice/Voucher"
                key={invoiceList.length}
                style={{ width: "100%" }}
                optionsList={(invoiceList || []).map((inv) => ({
                  sale_id: inv.pur_id,
                  display_text: inv.label,
                  raw: inv,
                }))}
                value={
                  selectedInvoice
                    ? {
                      value: selectedInvoice.label,
                      label: selectedInvoice.label,
                    }
                    : null
                }
                onSelect={(obj) => {
                  if (!obj?.raw) return;
                  dispatch(setSelectedInvoice(obj.raw));
                  dispatch(setPurIid(obj.raw.pur_id));

                  // If editing existing return (url contains id) -> fetch record                  

                  if (pur_r_id) {
                    // ✅ EDIT MODE → keep existing data intact
                    dispatch(fetchPurchaseReturnRecord(pur_r_id));
                  } else {
                    // ✅ CREATE MODE → load invoice items
                    dispatch(setFormField({ field: "itemCart", value: [] }));
                    dispatch(fetchInvoiceItemsForReturn(obj.raw.pur_id));
                  }
                }}
                disabled={!!pur_r_id}
                showAddNew={false}
              />
            </Form.Item>

            <Form.Item label="Institution Name">
              <InputField
                value={supplierInfo?.institution_name || ""}
                disabled
              />
            </Form.Item>
            <Form.Item label="Supplier Address">
              <InputField value={supplierInfo?.address || ""} disabled />
            </Form.Item>
            <Form.Item label="Contact No">
              <InputField value={supplierInfo?.contact_no || ""} disabled />
            </Form.Item>

            {isTatmeenUAE && hasTatmeenEntryAccess && (
              <Form.Item
                label="Parent SSCC Code"
              >
                <InputField
                  value={parentSsccCode || ""}
                  placeholder="Enter Parent SSCC Code"
                  onChange={(e) => {
                    dispatch(setParentSsccCode(e.target.value));
                  }}
                  // ✅ Add validation styling
                  style={{
                    borderColor: isTatmeen && !parentSsccCode ? '#ff4d4f' : undefined
                  }}
                />
                {/* {isTatmeen && !parentSsccCode && (
                  <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    Parent SSCC Code is required when Tatmeen is enabled
                  </div>
                )} */}
              </Form.Item>
            )}

            {isTatmeenUAE && hasTatmeenEntryAccess && (
              <Form.Item
                label="Tatmeen"
              >
                <Checkbox
                  checked={isTatmeen}
                  disabled
                  onChange={(e) => {
                    dispatch(setIsTatmeen(e.target.checked));
                    // If Tatmeen is enabled, show a message
                    if (e.target.checked) {
                      message.info("Tatmeen enabled. Please provide Child SSCC codes for returned items.");
                    }
                  }}
                >
                  Enable Tatmeen Tracking
                </Checkbox>
              </Form.Item>
            )}
          </Flex>

          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Voucher No.">
              <InputField
                value={voucherNumber}
                disabled
                data-testid="return-voucher-no"
              />
            </Form.Item>

            {/* <Form.Item
              label="Entry Date"
              name="entry_date"
              rules={[{ required: true, message: "Entry Date Is Requires" }]}
            >
              <DatePicker
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) => dispatch(setEntryDate(d))}
                style={{ width: "100%" }}
                format="DD-MM-YYYY" // <--- formatting handled here
              />
            </Form.Item> */}

            <Form.Item label="Entry Date" required>
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                disabledDate={disabledDate}
              />
            </Form.Item>

            <Form.Item label="Purchase Return Account" rules={[{ required: true }]}>
              <StaticAutoComplete
                optionsList={purchaseReturnAccs}
                value={selectedPurchaseReturnAcc}
                onSelect={(acc) => {
                  dispatch(setFormField({ field: "selectedPurchaseReturnAcc", value: acc }));
                  dispatch(setPurchaseReturnAcc(acc));
                }}
                placeholder="Select purchase return account"
                disabled={!purchaseReturnAccs?.length}
              />
            </Form.Item>
            <Form.Item label="Search Tags">
              <Select
                data-testid="roles-select"
                className="tag-multiselect"
                maxTagCount="responsive"
                mode="multiple"
                style={{ width: "100%" }}
                onChange={(e, options) => handletaggroup(e, options)}
                value={tag_field_id || null}
                placeholder="Select Tags"
                options={tags}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>

            <Form.Item label="Discount" style={{ display: "none" }}>
              <Radio.Group
                options={[
                  { label: "On Total", value: "on_total" },
                  { label: "On Individual Item", value: "on_item" },
                ]}
                onChange={(e) => {
                  dispatch(setTaxMode(e.target.value));
                  dispatch(calculateTotals({ authInfo }));
                }}
                value={taxMode}
              />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>

      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ groupId, createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t?.raw?.field_name?.toLowerCase() ===
                createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              setTagModal(false);
              return;
            }

            if (tag_field_id.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              setTagModal(false);
              return;
            }

            // ✅ New tag OR existing but not selected
            dispatch(setTagFeildNameid([...tag_field_id, match.id]));
            message.success("Tag added");

            setTagModal(false);
          }}
        />
      </AddNewModal>
      <Drawer
        title="Purchase Return Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Purchase Return
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Purchase Return module is used to record and manage returned
            purchase items within the ERP system. It helps update supplier
            balances, inventory stock, and financial records accurately.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Steps to Create Purchase Return</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>Purchase → Purchase Return</strong></li>
              <li>Select the supplier</li>
              <li>Choose the related purchase invoice</li>
              <li>Verify supplier and invoice details</li>
              <li>Enter return quantity for items</li>
              <li>Review taxes, discounts, and totals</li>
              <li>Select purchase return account</li>
              <li>Review all return details carefully</li>
              <li>Click <strong>Save</strong> to complete the return entry</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>A valid purchase invoice is required for processing returns.</li>
              <li>Returned quantity cannot exceed purchased quantity.</li>
              <li>Inventory stock updates automatically after saving.</li>
              <li>Verify item quantities and supplier details carefully.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CreatePurchaseReturnForm;
